import { Waves, Zap, BarChart3 } from 'lucide-react';
import EquipmentAdminValidation from '@/components/EquipmentAdminValidation';

export default function AdminEQValidation() {
  return (
    <EquipmentAdminValidation
      title="Equalization Tank Validation — Admin Audit"
      description="Peak shaving, mixing physics, and detention time calculations for flow balancing. Every equation, every value, every check."
      buildSections={(p) => {
        const flowRate = Number(p.flowRate ?? 250);
        const influentCOD = Number(p.cod ?? 3000);
        const influentBOD = Number(p.bod ?? 1500);
        const influentTSS = Number(p.tss ?? 250);
        const temperature = Number(p.temp ?? 20);
        const Q = flowRate;
        const q = Q / 24;
        const fmt = (v: any, d = 2) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

        // Default EQ geometry for validation
        const tankLength = 8, tankWidth = 4, tankDepth = 3.5, freeboard = 0.5;
        const workingVolumePercent = 90;
        const peakFactor = 2.5, minFlow = 80;
        const mixerType = 'mechanical', mixerPower = 2.5;

        const tankArea = tankLength * tankWidth;
        const totalVolume = tankArea * (tankDepth + freeboard);
        const workingVolume = tankArea * tankDepth * (workingVolumePercent / 100);
        const avgDetention = workingVolume / q;
        const peakQ = q * peakFactor;
        const minQ = minFlow / 24;
        const peakDetention = workingVolume / peakQ;
        const minDetention = workingVolume / minQ;
        const hlr = q / tankArea;
        const peakHLR = peakQ / tankArea;
        const volumeTurnover = Q / workingVolume;
        const peakDuration = 2;
        const peakShaveVolume = (peakQ - q) * peakDuration;
        const peakShavePercent = (peakShaveVolume / workingVolume) * 100;
        const codLoad = (Q * influentCOD) / 1000;
        const bodLoad = (Q * influentBOD) / 1000;
        const tssLoad = (Q * influentTSS) / 1000;
        const tempK = temperature + 273.15;
        const mu = 0.001 * Math.exp(-1.5 * (temperature - 20) / tempK);
        const mixerPowerW = mixerPower * 1000;
        const gValue = mixerType !== 'none' && mixerPower > 0
          ? Math.sqrt(mixerPowerW / (mu * workingVolume)) : 0;
        const mixingTime = q > 0 ? (workingVolume / q) * 60 : 0;
        const turnoverTime = workingVolume / q;

        return [
          {
            title: 'Hydraulics — Detention Time & Peak Shaving',
            icon: Waves,
            equations: [
              { label: 'Average flow', formula: 'q = Q / 24', substitution: `q = ${fmt(Q, 0)} / 24 = ${fmt(q, 1)} m³/h`, result: fmt(q, 1), status: 'pass', unit: 'm³/h' },
              { label: 'Peak flow', formula: 'q_peak = q × peakFactor', substitution: `q_peak = ${fmt(q, 1)} × ${peakFactor} = ${fmt(peakQ, 1)} m³/h`, result: fmt(peakQ, 1), status: 'pass', unit: 'm³/h' },
              { label: 'Tank area', formula: 'A = L × W', substitution: `A = ${tankLength} × ${tankWidth} = ${fmt(tankArea, 1)} m²`, result: fmt(tankArea, 1), status: 'pass', unit: 'm²' },
              { label: 'Working volume', formula: 'V_w = A × depth × (working%/100)', substitution: `V_w = ${fmt(tankArea, 1)} × ${tankDepth} × ${workingVolumePercent / 100} = ${fmt(workingVolume, 1)} m³`, result: fmt(workingVolume, 1), status: 'pass', unit: 'm³' },
              { label: 'Avg detention', formula: 't = V_w / q', substitution: `t = ${fmt(workingVolume, 1)} / ${fmt(q, 1)} = ${fmt(avgDetention, 1)} h`, result: fmt(avgDetention, 1), status: avgDetention >= 4 ? 'pass' : 'warn', unit: 'h' },
              { label: 'Peak detention', formula: 't_peak = V_w / q_peak', substitution: `t_peak = ${fmt(workingVolume, 1)} / ${fmt(peakQ, 1)} = ${fmt(peakDetention, 1)} h`, result: fmt(peakDetention, 1), status: peakDetention >= 1 ? 'pass' : 'warn', unit: 'h' },
              { label: 'HLR', formula: 'HLR = q / A', substitution: `HLR = ${fmt(q, 1)} / ${fmt(tankArea, 1)} = ${fmt(hlr, 2)} m³/m²·h`, result: fmt(hlr, 2), status: 'pass', unit: 'm³/m²·h' },
              { label: 'Peak HLR', formula: 'HLR_peak = q_peak / A', substitution: `HLR_peak = ${fmt(peakQ, 1)} / ${fmt(tankArea, 1)} = ${fmt(peakHLR, 2)} m³/m²·h`, result: fmt(peakHLR, 2), status: 'pass', unit: 'm³/m²·h' },
              { label: 'Volume turnover', formula: 'turnover = Q / V_w', substitution: `turnover = ${fmt(Q, 0)} / ${fmt(workingVolume, 1)} = ${fmt(volumeTurnover, 1)} ×/day`, result: fmt(volumeTurnover, 1), status: volumeTurnover <= 10 ? 'pass' : 'warn', unit: '×/day' },
              { label: 'Peak shave volume', formula: 'V_shave = (q_peak − q) × 2h', substitution: `V_shave = (${fmt(peakQ, 1)} − ${fmt(q, 1)}) × 2 = ${fmt(peakShaveVolume, 1)} m³`, result: fmt(peakShaveVolume, 1), status: 'pass', unit: 'm³' },
              { label: 'Peak shave %', formula: '% = V_shave / V_w × 100', substitution: `% = ${fmt(peakShaveVolume, 1)} / ${fmt(workingVolume, 1)} × 100 = ${fmt(peakShavePercent, 1)}%`, result: fmt(peakShavePercent, 1), status: peakShavePercent <= 50 ? 'pass' : 'warn', unit: '%' },
            ],
          },
          {
            title: 'Mixing Physics — Camp-Stein G-Value',
            icon: Zap,
            equations: [
              { label: 'Water viscosity', formula: 'μ = 0.001 × exp[−1.5×(T−20)/(T+273.15)]', substitution: `μ = 0.001 × exp[−1.5×${temperature - 20}/${fmt(tempK, 1)}] = ${fmt(mu * 1000, 3)} mPa·s`, result: fmt(mu * 1000, 3), status: 'pass', unit: 'mPa·s' },
              { label: 'Mixer power', formula: 'P = mixerPower × 1000', substitution: `P = ${mixerPower} × 1000 = ${fmt(mixerPowerW, 0)} W`, result: fmt(mixerPowerW, 0), status: 'pass', unit: 'W' },
              { label: 'G-value', formula: 'G = √(P / μV)', substitution: `G = √(${fmt(mixerPowerW, 0)} / (${fmt(mu, 5)} × ${fmt(workingVolume, 1)})) = ${fmt(gValue, 0)}`, result: fmt(gValue, 0), status: mixerType === 'none' ? 'fail' : gValue >= 15 ? 'pass' : 'warn', unit: 's⁻¹' },
              { label: 'Mixing time', formula: 't_mix = (V_w / q) × 60', substitution: `t_mix = (${fmt(workingVolume, 1)} / ${fmt(q, 1)}) × 60 = ${fmt(mixingTime, 0)} min`, result: fmt(mixingTime, 0), status: 'pass', unit: 'min' },
              { label: 'Turnover time', formula: 't_turn = V_w / q', substitution: `t_turn = ${fmt(workingVolume, 1)} / ${fmt(q, 1)} = ${fmt(turnoverTime, 1)} h`, result: fmt(turnoverTime, 1), status: 'pass', unit: 'h' },
            ],
          },
          {
            title: 'Mass Loading & Volume Turnover',
            icon: BarChart3,
            equations: [
              { label: 'COD load', formula: 'L_COD = Q × COD / 1000', substitution: `L_COD = ${fmt(Q, 0)} × ${influentCOD} / 1000 = ${fmt(codLoad, 1)} kg/d`, result: fmt(codLoad, 1), status: 'pass', unit: 'kg/d' },
              { label: 'BOD load', formula: 'L_BOD = Q × BOD / 1000', substitution: `L_BOD = ${fmt(Q, 0)} × ${influentBOD} / 1000 = ${fmt(bodLoad, 1)} kg/d`, result: fmt(bodLoad, 1), status: 'pass', unit: 'kg/d' },
              { label: 'TSS load', formula: 'L_TSS = Q × TSS / 1000', substitution: `L_TSS = ${fmt(Q, 0)} × ${influentTSS} / 1000 = ${fmt(tssLoad, 1)} kg/d`, result: fmt(tssLoad, 1), status: 'pass', unit: 'kg/d' },
              { label: 'Freeboard volume', formula: 'V_fb = V_total − V_w', substitution: `V_fb = ${fmt(totalVolume, 1)} − ${fmt(workingVolume, 1)} = ${fmt(totalVolume - workingVolume, 1)} m³`, result: fmt(totalVolume - workingVolume, 1), status: 'pass', unit: 'm³' },
            ],
          },
        ];
      }}
    />
  );
}
